import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { endOfDay, subDays } from 'date-fns';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  Subject,
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  takeUntil,
} from 'rxjs';
import { EnumService, PartyTypeItem } from 'src/app/services/enum/enum.service';
import {
  ClientForm,
  ClientFormValue,
} from 'src/app/services/form/form.controls';
import { CLIENT_FORM_NAME } from 'src/app/services/form/form.factory';
import { FormService } from 'src/app/services/form/form.service';
import {
  ClientsService,
  SelectedClient,
} from 'src/app/services/clients/clients.service';
import {
  CompanySearchAutocompleteItem,
  CompanySearchService,
} from 'src/app/shared/company-search-service/company-search.service';
import { PayeeIconComponent } from '../../../../shared/payee-icon/payee-icon.component';
import { MatIcon } from '@angular/material/icon';
import {
  MatAutocompleteOrigin,
  MatAutocompleteTrigger,
  MatAutocomplete,
  MatOption,
} from '@angular/material/autocomplete';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatError } from '@angular/material/input';
import { PayeeFormFieldComponent } from '../../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeDropdownComponent } from '../../../../shared/payee-form-fields/payee-dropdown/payee-dropdown.component';
import { PayeeDatepickerComponent } from '../../../../shared/payee-form-fields/payee-datepicker/payee-datepicker.component';
import { PayeeButtonComponent } from '../../../../shared/payee-buttons/payee-button/payee-button.component';
import { PayeeAutocompleteComponent } from '../../../../shared/payee-form-fields/payee-autocomplete/payee-autocomplete.component';
import { CountryFlagPipe } from '../../../../shared/pipes/country-flag/country-flag.pipe';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { AddClientDialogComponent } from 'src/app/dialogs/profile/add-client-dialog/add-client-dialog.component';

@Component({
  selector: 'app-client-form',
  templateUrl: './client-form.component.html',
  styleUrls: ['./client-form.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    PayeeIconComponent,
    MatIcon,
    MatAutocompleteOrigin,
    MatAutocompleteTrigger,
    MatProgressSpinner,
    MatAutocomplete,
    MatOption,
    MatError,
    PayeeFormFieldComponent,
    PayeeDropdownComponent,
    PayeeDatepickerComponent,
    PayeeButtonComponent,
    PayeeAutocompleteComponent,
    TranslateModule,
    CountryFlagPipe,
  ],
})
export class ClientFormComponent implements OnInit, OnDestroy {
  private formService = inject(FormService);
  private enumService = inject(EnumService);
  private companySearchService = inject(CompanySearchService);
  private clientsService = inject(ClientsService);
  private translate = inject(TranslateService);
  private snackbar = inject(MatSnackBar);
  private dialog = inject(MatDialog);

  readonly collapsed = this.formService.clientCollapsed;
  readonly loading = signal(true);
  readonly assigning = signal(false);
  readonly companyUpdateLoading = signal(false);

  private readonly destroy = new Subject<void>();
  private readonly companySearch$ = new Subject<string>();
  private clientMissingHandled = false;

  get form(): ClientForm {
    return this.formService.form.controls[CLIENT_FORM_NAME]!;
  }
  get client(): ClientFormValue {
    return this.form.getRawValue();
  }

  get showSelector(): boolean {
    return !this.formService.isGuest;
  }

  get showEditor(): boolean {
    return this.formService.isGuest || this.form.enabled;
  }

  get showNotAllowedToEditError(): boolean {
    return !this.formService.clientAllowsForm;
  }

  partyTypeOptions: PartyTypeItem[] = [];
  countryOptions: string[] = [];
  clientOptions: SelectedClient[] = [];
  readonly selectedClientControl = new FormControl<SelectedClient | null>(
    null,
    Validators.required,
  );

  get canExpand(): boolean {
    return this.form.enabled;
  }
  get showSaveRequiredError(): boolean {
    return this.formService.clientSaveRequiredError && !this.collapsed();
  }
  get addressSummary(): string {
    const address = this.client.address;
    return [address?.postcode, address?.settlement, address?.street]
      .filter(Boolean)
      .join(' ');
  }
  addressVisible = false;

  readonly maxBirthDate = endOfDay(subDays(new Date(), 1));

  get client_type() {
    return this.form.controls.client_type;
  }
  get name() {
    return this.form.controls.name;
  }
  get tax_number() {
    return this.form.controls.tax_number;
  }
  get party_type_id() {
    return this.form.controls.party_type_id;
  }
  get mother_name() {
    return this.form.controls.mother_name;
  }
  get birth_place() {
    return this.form.controls.birth_place;
  }
  get birth_date() {
    return this.form.controls.birth_date;
  }
  get country_iso() {
    return this.form.controls.address.controls.country_iso;
  }
  get postcode() {
    return this.form.controls.address.controls.postcode;
  }
  get settlement() {
    return this.form.controls.address.controls.settlement;
  }
  get street() {
    return this.form.controls.address.controls.street;
  }

  companySearchOptions: CompanySearchAutocompleteItem[] = [];
  get autocompleteSearchLoading(): boolean {
    return this.companySearchService.autocompleteSearchLoading;
  }

  get companySearchEnabled(): boolean {
    return this.country_iso?.value === 'HU';
  }

  async ngOnInit(): Promise<void> {
    this.formService.formChange
      .pipe(
        takeUntil(this.destroy),
        filter(() => !!this.form),
      )
      .subscribe(() => {
        this.syncSelectedClientControl();
        this.formService.clientCollapsed.set(!this.form.invalid);
        this.addressVisible = !!this.name.value;
        void this.lookupCompanyByTaxNumberIfNeeded();
      });

    this.name.valueChanges
      .pipe(takeUntil(this.destroy))
      .subscribe(() => this.syncSelectedClientControl());

    this.selectedClientControl.valueChanges
      .pipe(takeUntil(this.destroy))
      .subscribe((client) => void this.onClientSelected(client));

    this.companySearch$
      .pipe(
        takeUntil(this.destroy),
        debounceTime(500),
        distinctUntilChanged(),
        filter((name) => !!name && !this.collapsed()),
      )
      .subscribe(async (name) => {
        try {
          this.companySearchOptions =
            await this.companySearchService.getCompanySearchAutocompleteItems(
              name,
            );
        } catch (error) {
          console.error('Error while company autocomplete', error);
        }
      });

    this.clientsService.clientsSubject
      .pipe(takeUntil(this.destroy))
      .subscribe((clients) => {
        if (clients === null) {
          return;
        }
        this.clientOptions = clients;
        this.syncSelectedClientControl();
      });

    try {
      const [partyTypes, countries] = await Promise.all([
        this.enumService.getPartyTypes(),
        this.enumService.getCountries(),
      ]);
      this.partyTypeOptions = partyTypes;
      this.countryOptions = countries.map((c) => c.iso);
    } catch (error) {
      console.error('Error while loading enums', error);
    } finally {
      this.loading.set(false);
      if (this.form) {
        this.formService.clientCollapsed.set(!this.form.invalid);
        this.addressVisible = !!this.name.value;
        this.syncSelectedClientControl();
        await this.lookupCompanyByTaxNumberIfNeeded();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
    this.companySearch$.complete();
  }

  onCompanySearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement | null)?.value ?? '';
    this.companySearch$.next(value);
  }

  expandForm(): void {
    if (this.canExpand) {
      this.formService.clientSaveRequiredError = false;
      this.formService.clientCollapsed.set(false);
    }
  }

  async saveAndCollapse(skipTaxLookup = false): Promise<void> {
    if (!this.form.valid || this.companyUpdateLoading()) {
      return;
    }
    this.formService.clientSaveRequiredError = false;

    if (!skipTaxLookup) {
      await this.lookupCompanyByTaxNumberIfNeeded();
    }

    if (!this.form.valid) {
      return;
    }

    this.formService.clientCollapsed.set(true);
  }

  companySelected(company: CompanySearchAutocompleteItem): void {
    this.patchCompany(company);
    void this.saveAndCollapse(true);
  }

  private patchCompany(company: CompanySearchAutocompleteItem): void {
    const value: Partial<ClientFormValue> = {
      client_type: 'org',
      name: company.short_name || company.name,
      tax_number: company.tax_number,
      party_type_id: company.party_type?.internal_id ?? null,
      address: {
        country_iso: company.country?.iso ?? null,
        postcode: company.postcode,
        settlement: company.settlement,
        street: company.street,
      },
    };
    this.form.patchValue(value);
    this.addressVisible = true;
  }

  onNameBlur(): void {
    if (this.name.value) {
      this.addressVisible = true;
    }
  }

  companyDisplayWith(company: CompanySearchAutocompleteItem | string): string {
    if (typeof company === 'string') return company;
    return company?.short_name || company?.name || '';
  }

  clientDisplayWith = (client?: SelectedClient | null): string => {
    return client?.name ?? '';
  };

  addClient(): void {
    this.dialog
      .open(AddClientDialogComponent, { autoFocus: false })
      .afterClosed()
      .pipe(first(), takeUntil(this.destroy))
      .subscribe((client: SelectedClient | undefined) => {
        if (client) {
          void this.onClientSelected(client);
        }
      });
  }

  async onClientSelected(client: SelectedClient | null): Promise<void> {
    if (
      !client?.id ||
      this.assigning() ||
      client.id === this.formService.case?.client_id
    ) {
      return;
    }

    this.assigning.set(true);
    try {
      await this.formService.assignClient(client.id);
      this.syncSelectedClientControl();
    } catch (error) {
      console.error('Error while assigning client', error);
      this.syncSelectedClientControl();
    } finally {
      this.assigning.set(false);
    }
  }

  formatTaxNumber(taxNumber: string): string {
    if (!taxNumber) return '';
    const digits = taxNumber.replace(/\D/g, '');
    if (digits.length === 11) {
      return `${digits.slice(0, 8)}-${digits.slice(8, 9)}-${digits.slice(9, 11)}`;
    }
    return taxNumber;
  }

  formatCompanyAddress(option: CompanySearchAutocompleteItem): string {
    return [option.postcode, option.settlement, option.street].join(' ').trim();
  }

  readonly countryDisplayWith = (iso: string): string => {
    return this.translate.instant(`COUNTRY_NAMES.${iso}`, {
      default: new Intl.DisplayNames(this.translate.currentLang, {
        type: 'region',
      }).of(iso),
    });
  };

  private toTaxNumberRoot(taxNumber: string | null | undefined): string {
    return (taxNumber ?? '').replace(/\D/g, '').slice(0, 8);
  }

  private shouldLookupCompanyByTaxNumber(
    taxNumber: string | null | undefined,
  ): boolean {
    if (
      !taxNumber
      || !this.companySearchEnabled
      || this.client.client_type !== 'org'
    ) {
      return false;
    }

    return this.toTaxNumberRoot(taxNumber).length >= 8;
  }

  private async lookupCompanyByTaxNumberIfNeeded(): Promise<void> {
    if (!this.form) {
      return;
    }

    const taxNumber = this.tax_number.value;
    if (!this.shouldLookupCompanyByTaxNumber(taxNumber)) {
      return;
    }

    if (this.companyUpdateLoading()) {
      return;
    }

    this.companyUpdateLoading.set(true);
    try {
      const company = await this.companySearchService.exactSearch(taxNumber);
      if (!company) {
        return;
      }

      this.patchCompany(company);
    } catch (error) {
      console.error('Error while exact company lookup by tax number', error);
    } finally {
      this.companyUpdateLoading.set(false);
    }
  }

  private syncSelectedClientControl(): void {
    if (!this.form || this.clientsService.clientsSubject.value === null) {
      return;
    }

    const currentId = this.formService.case?.client_id;
    if (!currentId) {
      this.selectedClientControl.setValue(null, { emitEvent: false });
      return;
    }

    const selectedIndex = this.clientOptions.findIndex(
      (c) => c.id === currentId,
    );
    if (selectedIndex === -1) {
      void this.handleMissingClient();
      return;
    }

    const selected = this.clientOptions[selectedIndex];
    const formClient = this.client;
    const syncedClient: SelectedClient = {
      ...selected,
      name: formClient.name ?? selected.name,
      tax_number: formClient.tax_number ?? selected.tax_number,
      country_iso: formClient.address?.country_iso ?? selected.country_iso,
    };

    if (
      syncedClient.name !== selected.name
      || syncedClient.tax_number !== selected.tax_number
      || syncedClient.country_iso !== selected.country_iso
    ) {
      this.clientOptions = [
        ...this.clientOptions.slice(0, selectedIndex),
        syncedClient,
        ...this.clientOptions.slice(selectedIndex + 1),
      ];
    }

    this.selectedClientControl.setValue(syncedClient, { emitEvent: false });
  }

  private async handleMissingClient(): Promise<void> {
    if (this.clientMissingHandled || this.formService.isGuest) {
      return;
    }
    this.clientMissingHandled = true;
    this.snackbar.open(
      this.translate.instant('CASE_FORM.CLIENT.CLIENT_NOT_FOUND'),
      this.translate.instant('OK'),
      { duration: 10_000 },
    );
    await this.formService.redirectToCases();
  }
}
